export type ReachabilityState = 'reachable' | 'unreachable'
type Subscriber = (state: ReachabilityState) => void

const POLL_INTERVAL_MS = 2500
const HEALTH_TIMEOUT_MS = 3000
const HEALTH_URL = '/api/health'

function createServerReachability() {
  let state: ReachabilityState = 'reachable'
  let polling = false
  let intervalId: ReturnType<typeof setInterval> | null = null
  const subscribers = new Set<Subscriber>()

  function notify(): void {
    for (const fn of subscribers) {
      fn(state)
    }
  }

  async function healthCheck(): Promise<boolean> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), HEALTH_TIMEOUT_MS)

    try {
      const response = await fetch(HEALTH_URL, { signal: controller.signal })
      clearTimeout(timeoutId)

      // Captive portal: got a response but it's not JSON from our server
      const contentType = response.headers.get('content-type') ?? ''
      if (!contentType.includes('application/json')) return false

      // Server responded but DB is down (503)
      if (!response.ok) return false

      const data = await response.json()
      return data?.status === 'ok'
    } catch {
      clearTimeout(timeoutId)
      return false
    }
  }

  function startPolling(): void {
    if (polling) return
    polling = true

    intervalId = setInterval(async () => {
      const ok = await healthCheck()
      if (ok) reportSuccess()
    }, POLL_INTERVAL_MS)
  }

  function stopPolling(): void {
    if (!polling) return
    polling = false
    if (intervalId !== null) {
      clearInterval(intervalId)
      intervalId = null
    }
  }

  function reportFailure(): void {
    if (state === 'unreachable') return
    state = 'unreachable'
    notify()
    startPolling()
  }

  function reportSuccess(): void {
    stopPolling()
    if (state === 'reachable') return
    state = 'reachable'
    notify()
  }

  async function checkNow(): Promise<void> {
    const ok = await healthCheck()
    if (ok) reportSuccess()
  }

  function subscribe(fn: Subscriber): () => void {
    subscribers.add(fn)
    return () => {
      subscribers.delete(fn)
    }
  }

  function getState(): ReachabilityState {
    return state
  }

  // Browser adapter-level events (covers airplane mode, WiFi toggle)
  window.addEventListener('offline', () => {
    reportFailure()
  })

  window.addEventListener('online', () => {
    // Don't trust it — just trigger an immediate check instead of waiting for the next interval
    checkNow()
  })

  return { getState, subscribe, reportFailure, reportSuccess, checkNow }
}

export const serverReachability = createServerReachability()
